import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-forest-green via-midnight-green to-deep-teal">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Button asChild variant="copper" className="mb-6">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </Button>

        <Card className="bg-sand/95 backdrop-blur-sm border-deep-teal/20">
          <ScrollArea className="h-[calc(100vh-12rem)] px-8 py-6">
            <div className="prose prose-slate max-w-none">
              <h1 className="text-4xl font-bold text-midnight-green mb-2">Terms of Service</h1>
              <p className="text-sm text-forest-green/70 mb-8">
                <strong>Effective Date:</strong> January 1, 2025<br />
                <strong>Last Updated:</strong> January 1, 2025
              </p>

              {/* Table of Contents */}
              <Card className="bg-cream/50 p-6 mb-8 border-copper/20">
                <h2 className="text-xl font-semibold text-midnight-green mt-0 mb-4">Table of Contents</h2>
                <nav className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <a href="#agreement" className="text-deep-teal hover:text-copper transition-colors">1. Agreement to Terms</a>
                  <a href="#service-description" className="text-deep-teal hover:text-copper transition-colors">2. Service Description</a>
                  <a href="#eligibility" className="text-deep-teal hover:text-copper transition-colors">3. Eligibility and Account Registration</a>
                  <a href="#subscription" className="text-deep-teal hover:text-copper transition-colors">4. Subscription and Payment Terms</a>
                  <a href="#user-conduct" className="text-deep-teal hover:text-copper transition-colors">5. User Conduct and Acceptable Use</a>
                  <a href="#intellectual-property" className="text-deep-teal hover:text-copper transition-colors">6. Intellectual Property Rights</a>
                  <a href="#privacy" className="text-deep-teal hover:text-copper transition-colors">7. Privacy and Data Protection</a>
                  <a href="#third-party" className="text-deep-teal hover:text-copper transition-colors">8. Third-Party Services</a>
                  <a href="#disclaimers" className="text-deep-teal hover:text-copper transition-colors">9. Disclaimers and Limitations of Liability</a>
                  <a href="#indemnification" className="text-deep-teal hover:text-copper transition-colors">10. Indemnification</a>
                  <a href="#termination" className="text-deep-teal hover:text-copper transition-colors">11. Term and Termination</a>
                  <a href="#modifications" className="text-deep-teal hover:text-copper transition-colors">12. Modifications to Service and Terms</a>
                </nav>
              </Card>

              <section id="agreement" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">1. Agreement to Terms</h2>
                <p className="text-forest-green">
                  These Terms of Service ("Terms") constitute a legally binding agreement between you (whether personally or on behalf of an entity) and PA Advisor, LLC, a California limited liability company ("Company," "we," "us," or "our"), concerning your access to and use of the CampChef Pro website, application, and services (collectively, the "Service").
                </p>
                <p className="text-forest-green">
                  By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of these Terms, then you may not access the Service.
                </p>
                <div className="bg-copper/10 p-4 rounded-lg border border-copper/30 my-4">
                  <p className="text-midnight-green font-semibold">
                    <strong>IMPORTANT:</strong> THESE TERMS CONTAIN A BINDING ARBITRATION PROVISION AND CLASS ACTION WAIVER. THESE AFFECT YOUR LEGAL RIGHTS AS DETAILED IN SECTION 19.
                  </p>
                </div>
              </section>

              <section id="service-description" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">2. Service Description</h2>
                <p className="text-forest-green">
                  CampChef Pro is a meal planning, recipe management, and food ordering system designed for camps and similar organizations. The Service includes:
                </p>
                <ul className="text-forest-green">
                  <li>Recipe and menu creation tools</li>
                  <li>Inventory management</li>
                  <li>Order generation and submission</li>
                  <li>Multi-user collaboration features</li>
                  <li>Integration with third-party suppliers</li>
                </ul>
              </section>

              <section id="eligibility" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">3. Eligibility and Account Registration</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">3.1 Eligibility</h3>
                <p className="text-forest-green">
                  You must be at least 18 years old and capable of forming a binding contract to use the Service. By using the Service, you represent and warrant that you meet these requirements.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">3.2 Organization Accounts</h3>
                <p className="text-forest-green">
                  The Service is provided on an enterprise basis. Organizations must contact our sales team to establish an account. Individual users are provisioned by enterprise administrators.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">3.3 Account Security</h3>
                <p className="text-forest-green">You are responsible for:</p>
                <ul className="text-forest-green">
                  <li>Maintaining the confidentiality of your login credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of any unauthorized use</li>
                  <li>Ensuring your account information is accurate and current</li>
                </ul>
              </section>

              <section id="subscription" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">4. Subscription and Payment Terms</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">4.1 Subscription Plans</h3>
                <p className="text-forest-green">The Service is offered on a subscription basis with the following tiers:</p>
                <ul className="text-forest-green">
                  <li><strong>Starter:</strong> $99/month</li>
                  <li><strong>Professional:</strong> $299/month</li>
                  <li><strong>Enterprise:</strong> $999/month</li>
                  <li><strong>Custom:</strong> Contact sales</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">4.2 Payment</h3>
                <ul className="text-forest-green">
                  <li>Payment is processed through our third-party provider, Stripe</li>
                  <li>Subscriptions automatically renew unless cancelled</li>
                  <li>All fees are in US dollars and exclusive of taxes</li>
                  <li>You are responsible for all applicable taxes</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">4.3 Cancellation and Refunds</h3>
                <ul className="text-forest-green">
                  <li>You may cancel your subscription at any time</li>
                  <li>Cancellation takes effect at the end of the current billing period</li>
                  <li>No refunds for partial months or unused services</li>
                  <li>We reserve the right to modify pricing with 30 days' notice</li>
                </ul>
              </section>

              <section id="user-conduct" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">5. User Conduct and Acceptable Use</h2>
                <p className="text-forest-green">You agree not to:</p>
                <ul className="text-forest-green">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Use the Service for unauthorized commercial purposes</li>
                  <li>Attempt to gain unauthorized access to any systems</li>
                  <li>Interfere with or disrupt the Service</li>
                  <li>Upload malicious code or harmful content</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Misrepresent your identity or affiliation</li>
                  <li>Share your account credentials</li>
                  <li>Use automated systems to access the Service without permission</li>
                </ul>
              </section>

              <section id="intellectual-property" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">6. Intellectual Property Rights</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">6.1 Our Intellectual Property</h3>
                <p className="text-forest-green">
                  The Service and its original content, features, and functionality are owned by PA Advisor, LLC and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">6.2 Your Content</h3>
                <p className="text-forest-green">
                  You retain ownership of content you create using the Service ("User Content"). By using the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, store, and process your User Content solely to provide the Service.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">6.3 Feedback</h3>
                <p className="text-forest-green">
                  Any feedback, suggestions, or ideas you provide about the Service become our property and may be used without compensation or attribution.
                </p>
              </section>

              <section id="privacy" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">7. Privacy and Data Protection</h2>
                <p className="text-forest-green">
                  Your use of the Service is also governed by our <Link to="/privacy" className="text-copper hover:underline font-semibold">Privacy Policy</Link>, which is incorporated by reference into these Terms. By using the Service, you consent to our collection and use of data as described in the Privacy Policy.
                </p>
              </section>

              <section id="third-party" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">8. Third-Party Services</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">8.1 Integrations</h3>
                <p className="text-forest-green">
                  The Service may integrate with third-party services (e.g., Sysco for food ordering). Your use of these integrations is subject to the third parties' terms and conditions.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">8.2 Third-Party Links</h3>
                <p className="text-forest-green">
                  We are not responsible for the content or practices of third-party websites or services linked from our Service.
                </p>
              </section>

              <section id="disclaimers" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">9. Disclaimers and Limitations of Liability</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">9.1 Service Availability</h3>
                <p className="text-forest-green">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">9.2 Food Safety Disclaimer</h3>
                <p className="text-forest-green">While the Service assists with meal planning and ordering, you are solely responsible for:</p>
                <ul className="text-forest-green">
                  <li>Food safety and handling</li>
                  <li>Compliance with health regulations</li>
                  <li>Allergen management</li>
                  <li>Nutritional accuracy</li>
                  <li>Food quality and preparation</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">9.3 Limitation of Liability</h3>
                <p className="text-forest-green">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, PA ADVISOR, LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                </p>
                <p className="text-forest-green">
                  IN NO EVENT SHALL OUR AGGREGATE LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY.
                </p>
              </section>

              <section id="indemnification" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">10. Indemnification</h2>
                <p className="text-forest-green">
                  You agree to defend, indemnify, and hold harmless PA Advisor, LLC, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:
                </p>
                <ul className="text-forest-green">
                  <li>Your use of the Service</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any rights of another party</li>
                  <li>Your User Content</li>
                </ul>
              </section>

              <section id="termination" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">11. Term and Termination</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">11.1 Term</h3>
                <p className="text-forest-green">These Terms remain in effect while you use the Service.</p>

                <h3 className="text-xl font-semibold text-deep-teal">11.2 Termination by You</h3>
                <p className="text-forest-green">You may stop using the Service at any time.</p>

                <h3 className="text-xl font-semibold text-deep-teal">11.3 Termination by Us</h3>
                <p className="text-forest-green">
                  We may suspend or terminate your access to the Service immediately, without prior notice or liability, for any reason, including:
                </p>
                <ul className="text-forest-green">
                  <li>Breach of these Terms</li>
                  <li>Non-payment of fees</li>
                  <li>Suspected fraudulent or illegal activity</li>
                  <li>Extended period of inactivity</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">11.4 Effect of Termination</h3>
                <p className="text-forest-green">Upon termination:</p>
                <ul className="text-forest-green">
                  <li>Your right to use the Service ceases immediately</li>
                  <li>We may delete your data after a reasonable retention period</li>
                  <li>All provisions that should survive termination shall remain in effect</li>
                </ul>
              </section>

              <section id="modifications" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">12. Modifications to Service and Terms</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">12.1 Service Changes</h3>
                <p className="text-forest-green">
                  We reserve the right to modify, suspend, or discontinue the Service at any time with reasonable notice.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">12.2 Terms Updates</h3>
                <p className="text-forest-green">
                  We may update these Terms from time to time. Material changes will be notified via email or Service notification. Continued use after changes constitutes acceptance.
                </p>
              </section>

              <section id="contact" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">20. Contact Information</h2>
                <p className="text-forest-green">For questions about these Terms, please contact:</p>
                <div className="bg-cream/50 p-4 rounded-lg border border-copper/20 my-4">
                  <p className="text-forest-green mb-2"><strong>PA Advisor, LLC</strong></p>
                  <p className="text-forest-green">Email: <a href="mailto:info@paadvisor.com" className="text-copper hover:underline">info@paadvisor.com</a></p>
                  <p className="text-forest-green">Phone: 760-576-8002</p>
                  <p className="text-forest-green">Address: 1389 Sky Ridge Ct. San Marcos, CA 92078</p>
                </div>
                <p className="text-forest-green">
                  For support issues: <a href="mailto:info@paadvisor.com" className="text-copper hover:underline">info@paadvisor.com</a><br />
                  For sales inquiries: <a href="mailto:info@paadvisor.com" className="text-copper hover:underline">info@paadvisor.com</a>
                </p>
              </section>

              <div className="bg-copper/10 p-6 rounded-lg border-2 border-copper/30 mt-8">
                <p className="text-midnight-green font-semibold text-center">
                  BY USING CAMPCHEF PRO, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM.
                </p>
              </div>

              <div className="mt-6 text-sm text-forest-green/70">
                <p><strong>Version History:</strong></p>
                <p>Version 1.0 - January 1, 2025 - Initial Terms of Service</p>
              </div>
            </div>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
